import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";
import { gossipTestimonials } from "@/mocks/testimonials";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import SubscribeForm from "@/components/feature/SubscribeForm";

export default function About() {
  useSeo({
    title: "关于我们 - 网报吃瓜的定位、内容原则与整理方式",
    description:
      "了解网报吃瓜是什么：一个以事件串方式整理网络热点的内容站点。这里有我们的网站定位、发展理念、内容整理原则、整理历程与联系方式。",
    keywords: "网报吃瓜,关于网报吃瓜,网报吃瓜整理,吃瓜事件库,内容原则",
    canonical: "https://zhongjianshuyuan.cn/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于${siteInfo.name}`,
        url: "https://zhongjianshuyuan.cn/about",
        inLanguage: "zh-CN",
        description: "网报吃瓜的网站定位、发展理念、内容原则与整理方式介绍。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: "https://zhongjianshuyuan.cn/",
        email: siteInfo.email,
        description: siteInfo.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "网报吃瓜", item: "https://zhongjianshuyuan.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://zhongjianshuyuan.cn/about" },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full bg-background-100 px-4 pb-12 pt-28 md:px-8 md:pb-16 md:pt-36">
        <Breadcrumb items={[{ label: "网报吃瓜", to: "/" }, { label: "关于我们" }]} />
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] text-primary-600">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              ABOUT US
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              网报吃瓜：把散落的热点，整理成能按顺序读完的事件串
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteInfo.name}是一个专注于热点脉络整理的内容站点。我们不追求第一时间喊出结论，而是把同一件事的节点收在同一条记录里，
              按发生、发酵、回应、后续四段排好，让读者从上往下读一遍，就能自己看清这件事是怎么一步步走到今天的。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/events"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-list-check-2" />
                进入吃瓜事件库
              </Link>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap"
              >
                <i className="ri-chat-3-line" />
                联系与反馈
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <dl className="grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              {siteInfo.stats.map((item) => (
                <div key={item.label}>
                  <dt className="font-heading text-2xl font-bold text-primary-600">{item.value}</dt>
                  <dd className="mt-1 text-xs text-foreground-600">{item.label}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs leading-relaxed text-foreground-500">
              以上为站内运营统计口径，用于说明整理规模，不代表任何外部排名或权威认定。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-16 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-4">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              我们服务的是不想被碎片信息牵着走的人。对他们来说，重要的不是第一时间看到那条最刺激的截图，
              而是搞清楚这件事的前因后果、谁在什么时候说了什么、后来又是怎么收场的。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              所以网报吃瓜更像一本持续更新的观察笔记，而不是一个追热点的流量入口。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-user-heart-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">为谁整理</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                时间有限但不想错过重点的普通读者，以及需要快速回顾事件脉络的内容与市场从业者。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className="ri-eye-line text-xl" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">不做什么</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                不替读者下结论，不把单一信源的说法当成事实，也不用耸动标题换取点击。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="principles" className="w-full scroll-mt-20 bg-background-100 px-4 py-16 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          这四条是我们整理每一条事件串时都会对照执行的规则，也是读者判断本站内容可信度的依据。
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {siteInfo.principles.map((item, index) => (
            <Reveal key={item.title} delay={index * 70}>
              <div className="flex h-full gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="history" className="w-full scroll-mt-20 px-4 py-16 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">整理历程</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          从一个人给自己用的时间线表格，到现在公开浏览的事件库，中间其实只做了一件事：坚持把节点记全。
        </p>

        <ol className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {siteInfo.milestones.map((item, index) => (
            <Reveal key={item.year} delay={index * 70}>
              <li className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-heading text-2xl font-bold text-primary-500">{item.year}</span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </section>

      <section className="w-full bg-background-100 px-4 py-16 md:px-8 md:py-20">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者口碑</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          下面这些反馈来自长期使用事件库的读者，也是我们判断整理方向是否有用的重要参考。
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {gossipTestimonials.slice(0, 3).map((item, index) => (
            <Reveal key={item.name} delay={index * 70}>
              <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-300" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.quote}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={item.avatar}
                      alt={`${item.name}的头像插画`}
                      title={`${item.name} - 网报吃瓜读者口碑`}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <div id="contact" className="scroll-mt-20">
        <SubscribeForm compact />
      </div>
    </>
  );
}